/* ===========================================================================
   c-compare.js — vacuum comparison tool (Step Labs)
   ===========================================================================

   Enhancement only. sections/c-compare.liquid already renders every eligible
   product as a rail card and as a table column, and shows the default
   columns with real add-to-cart forms. This script never builds table markup.
   It:

     - reads each column's data attributes and each cell's data-v / data-text
     - filters the rail by the need chips (a model must match every chip)
     - opens the table with the section's default products (the same ones
       Liquid already shows; on a PDP the product leads and takes the place
       of one of them), and fills it with the top picks once a need is
       chosen, until the shopper changes it
     - switches columns on and off (`is-off`), orders them, shows the
       "Room for one more" slot while there is room
     - flags rows that are the same on all (`is-same`) for "Only differences"
       and, when the section asks for it, the feature leader per spec row
     - runs the dialogs from Figma 1989:1660: comparison full, replace (model
       picker), unavailable, added to cart, could not add, expert
     - adds to cart through /cart/add.js so the shopper stays on the page;
       the form still posts normally without this script
     - keeps the view in the URL (ids, needs, diff, src) with replaceState
     - on a PDP (sections/c-compare-embed.liquid), fetches the tool from the
       comparison page once the PDP has loaded and the browser is idle, mounts
       it with the product pinned to the first column when the shopper nears
       it, and leaves the PDP URL alone

   Thresholds come from the section settings through the JSON config block.
   =========================================================================== */
(function () {
  var SELECTOR = '[data-sl-id="sl-section-compare-v1"]';

  function track(name, props) {
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(Object.assign({ event: name }, props));
      if (window.Shopify && Shopify.analytics && Shopify.analytics.publish) Shopify.analytics.publish(name, props);
    } catch (e) { /* tracking must never break the tool */ }
  }

  function init(root) {
    if (root.__cCompare) return;
    root.__cCompare = true;

    var cfg = {};
    try { cfg = JSON.parse(root.querySelector('[data-compare-config]').textContent); } catch (e) { /* defaults below */ }
    var MAX = cfg.max || 4;
    var DEFAULT_COUNT = cfg.defaultCount || 3;
    var EMBEDDED = root.dataset.embed === '1';
    var PIN = root.dataset.pin || '';

    var board = root.querySelector('[data-board]');
    var slot = root.querySelector('[data-slot]');
    var rail = root.querySelector('[data-rail-track]');
    var scroller = root.querySelector('[data-scroller]');
    var labels = {};
    root.querySelectorAll('[data-label]').forEach(function (el) { labels[el.dataset.row] = el; });

    var picks = {};
    rail.querySelectorAll('[data-pick]').forEach(function (el) { picks[el.dataset.pick] = el; });

    var cols = [].map.call(board.querySelectorAll('[data-col]'), function (el, index) {
      var cells = {};
      el.querySelectorAll('.c-compare__cell').forEach(function (cell) {
        cells[cell.dataset.row] = { el: cell, kind: cell.dataset.kind, v: cell.dataset.v, text: cell.dataset.text || '', unit: cell.dataset.unit || '' };
      });
      var d = el.dataset;
      return {
        el: el, index: index, id: d.id, name: d.name, vendor: d.vendor,
        price: Number(d.price), available: d.available === 'true', power: d.power,
        best: d.best === 'true', needTags: d.needTags ? d.needTags.split('|') : [],
        cells: cells, form: el.querySelector('form'), pick: picks[d.id]
      };
    });
    if (!cols.length) return;

    var byId = {};
    cols.forEach(function (c) { byId[c.id] = c; });
    var DEFAULTS = (cfg.defaults || []).map(String).filter(function (id) { return byId[id]; }).slice(0, MAX);
    var rowKeys = Object.keys(cols[0].cells);

    function num(c, key) { return c.cells[key] ? parseFloat(c.cells[key].v) || 0 : 0; }
    function scoreChip(key) { return function (c) { return num(c, key) >= cfg.scoreMin; }; }
    var CHIPS = {
      'best-sellers': { label: 'best sellers', test: function (c) { return c.best; } },
      'pet-hair': { label: 'pet hair', test: scoreChip('score_pet'), key: 'score_pet' },
      'allergies': { label: 'allergies', test: scoreChip('score_allergy'), key: 'score_allergy' },
      'hard-floors': { label: 'hard floors', test: scoreChip('score_hard_floor'), key: 'score_hard_floor' },
      'deep-carpet': { label: 'deep carpet', test: scoreChip('score_carpet'), key: 'score_carpet' },
      'lightweight': { label: 'lightweight', test: function (c) { var w = num(c, 'weight_lbs'); return w > 0 && w <= cfg.lightMax; } },
      'cordless': { label: 'cordless', test: function (c) { return c.power === 'Cordless'; } }
    };
    var RATED = rowKeys.filter(function (key) { return cols[0].cells[key].kind === 'score'; });
    function matches(c, id) { return CHIPS[id].test(c) || c.needTags.indexOf(CHIPS[id].label) > -1; }

    // ---- state, restored from the URL -------------------------------------
    // Embedded on a PDP, the page URL belongs to the product, not to the tool.
    var query = new URLSearchParams(EMBEDDED ? 'src=pdp' : window.location.search);
    function list(name) { return (query.get(name) || '').split(',').filter(Boolean); }
    var state = {
      ids: list('ids').filter(function (id) { return byId[id]; }).slice(0, MAX),
      needs: list('needs').filter(function (id) { return CHIPS[id] && root.querySelector('[data-chip="' + id + '"]'); }),
      diff: query.get('diff') === '1',
      src: query.get('src') || '',
      auto: !query.get('ids'),   // the table follows the chips until the shopper edits it
      replacing: null            // id of the column the picker replaces
    };

    // ---- ranking: chips matched, best seller, summed scores, list order -----
    function ranked() {
      return cols.map(function (c) {
        return {
          c: c,
          matched: state.needs.filter(function (id) { return matches(c, id); }).length,
          // With no need picked, the five ratings together decide.
          sum: state.needs.length
            ? state.needs.reduce(function (total, id) { return total + (CHIPS[id].key ? num(c, CHIPS[id].key) : 0); }, 0)
            : RATED.reduce(function (total, key) { return total + num(c, key); }, 0)
        };
      }).sort(function (a, b) {
        return (b.matched - a.matched) || (b.c.available - a.c.available) || (b.c.best - a.c.best) ||
          (b.sum - a.sum) || (a.c.index - b.c.index);
      });
    }
    function shelf() {
      return ranked().filter(function (r) { return r.matched === state.needs.length; }).map(function (r) { return r.c; });
    }
    function autoFill() {
      // No need picked: the default products Liquid already put in the table.
      // On a PDP the product leads. When it is not one of the defaults, it
      // takes the place of the default of its own brand (else the last one),
      // so the brand mix the defaults were chosen for stays: a SEBO PDP still
      // meets the Miele, a Miele or Riccar PDP still meets both SEBOs.
      if (!state.needs.length && DEFAULTS.length > 1) {
        var ids = DEFAULTS.slice();
        if (PIN && byId[PIN]) {
          var at = ids.indexOf(PIN);
          if (at < 0) {
            at = ids.length - 1;
            for (var i = ids.length - 1; i >= 0; i--) {
              if (byId[ids[i]].vendor === byId[PIN].vendor) { at = i; break; }
            }
          }
          ids.splice(at, 1);
          ids.unshift(PIN);
        }
        state.ids = ids;
        state.closest = false;
        return;
      }
      var pool = ranked().filter(function (r) { return r.c.available; });
      var exact = pool.filter(function (r) { return r.matched === state.needs.length; });
      state.closest = state.needs.length > 0 && exact.length === 0;
      // One model per brand first, so the opening table is a real comparison;
      // then fill any remaining place in rank order.
      var chosen = [];
      var brands = [];
      pool.forEach(function (r) {
        if (chosen.length < DEFAULT_COUNT && brands.indexOf(r.c.vendor) < 0) { chosen.push(r.c); brands.push(r.c.vendor); }
      });
      pool.forEach(function (r) { if (chosen.length < DEFAULT_COUNT && chosen.indexOf(r.c) < 0) chosen.push(r.c); });
      chosen.sort(function (a, b) { return pool.findIndex(function (r) { return r.c === a; }) - pool.findIndex(function (r) { return r.c === b; }); });
      state.ids = chosen.map(function (c) { return c.id; });
      // On a PDP, the product being viewed always leads the table.
      if (PIN && byId[PIN]) {
        state.ids = [PIN].concat(state.ids.filter(function (id) { return id !== PIN; })).slice(0, DEFAULT_COUNT);
      }
    }

    // ---- feature leaders, spec rows only (Figma 1977:111) ----------------------
    function leaders(key, visible) {
      var kind = visible[0].cells[key].kind;
      var lowWins = kind === 'number_low' || kind === 'noise';
      if (['number_low', 'number_high', 'reach', 'noise', 'warranty'].indexOf(kind) < 0) return [];
      if (kind === 'reach' && visible.some(function (c) { return c.cells[key].unit !== visible[0].cells[key].unit; })) return [];
      var known = visible.filter(function (c) { return parseFloat(c.cells[key].v) > 0; });
      if (known.length < 2) return [];
      var values = known.map(function (c) { return parseFloat(c.cells[key].v); });
      var best = lowWins ? Math.min.apply(null, values) : Math.max.apply(null, values);
      var won = known.filter(function (c, i) { return values[i] === best; });
      return won.length === visible.length ? [] : won;
    }

    // ---- dialogs ----------------------------------------------------------------
    var dialogs = {};
    root.querySelectorAll('[data-dialog]').forEach(function (d) { dialogs[d.dataset.dialog] = d; });
    var lastForm = null;
    function open(name) {
      var d = dialogs[name];
      if (!d) return;
      if (d.open) d.close();
      if (d.showModal) d.showModal(); else d.setAttribute('open', '');
      track('compare_dialog', { dialog: name });
    }
    function close(d) { if (d.close) d.close(); else d.removeAttribute('open'); }
    Object.keys(dialogs).forEach(function (name) {
      // A click on the backdrop lands on the dialog itself.
      dialogs[name].addEventListener('click', function (event) { if (event.target === dialogs[name]) close(dialogs[name]); });
    });

    function openPicker(id) {
      state.replacing = id;
      var listEl = dialogs.picker.querySelector('[data-picker]');
      listEl.textContent = '';
      shelf().filter(function (c) { return state.ids.indexOf(c.id) < 0; }).slice(0, 24).forEach(function (c) {
        var li = document.createElement('li');
        var img = c.pick && c.pick.querySelector('img');
        if (img) {
          var thumb = document.createElement('img');
          thumb.src = img.currentSrc || img.src;
          thumb.alt = '';
          thumb.loading = 'lazy';
          li.appendChild(thumb);
        }
        var info = document.createElement('div');
        [['c-compare__meta', c.pick.querySelector('.c-compare__meta').textContent],
          ['c-compare__picker-name', c.name],
          ['c-compare__picker-price', c.pick.querySelector('.c-compare__price').textContent]].forEach(function (pair) {
          var p = document.createElement('p');
          p.className = pair[0];
          p.textContent = pair[1];
          info.appendChild(p);
        });
        li.appendChild(info);
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'c-compare__btn c-compare__btn--secondary';
        btn.dataset.choose = c.id;
        btn.textContent = 'Choose';
        li.appendChild(btn);
        listEl.appendChild(li);
      });
      open('picker');
    }

    // ---- render --------------------------------------------------------------
    function render() {
      var visible = state.ids.map(function (id) { return byId[id]; });

      cols.forEach(function (c) {
        var position = visible.indexOf(c);
        c.el.classList.toggle('is-off', position < 0);
        c.el.style.order = position < 0 ? '' : String(position);
      });
      slot.classList.toggle('is-off', visible.length >= MAX);

      rowKeys.forEach(function (key) {
        var same = visible.length > 1 && visible.every(function (c) { return c.cells[key].text === visible[0].cells[key].text; });
        var won = cfg.leaders && visible.length > 1 && !same ? leaders(key, visible) : [];
        cols.forEach(function (c) {
          var cell = c.cells[key].el;
          cell.classList.toggle('is-same', same);
          var lead = won.indexOf(c) > -1;
          cell.classList.toggle('is-leader', lead);
          var tag = cell.querySelector('.c-compare__lead');
          if (lead && !tag) {
            tag = document.createElement('span');
            tag.className = 'c-compare__lead';
            cell.appendChild(tag);
          }
          if (tag) {
            tag.hidden = !lead;
            tag.textContent = won.length > 1 ? '✓ Joint leader' : '✓ Feature leader';
          }
        });
        var slotCell = slot.querySelector('[data-row="' + key + '"]');
        if (slotCell) slotCell.classList.toggle('is-same', same);
        if (labels[key]) labels[key].classList.toggle('is-same', same);
      });

      root.classList.toggle('is-diff-on', state.diff);
      root.querySelector('[data-toggle="diff"]').setAttribute('aria-pressed', String(state.diff));

      root.querySelectorAll('[data-chip]').forEach(function (b) {
        b.setAttribute('aria-pressed', String(state.needs.indexOf(b.dataset.chip) > -1));
      });
      var onShelf = shelf();
      cols.forEach(function (c) {
        if (!c.pick) return;
        var position = onShelf.indexOf(c);
        c.pick.hidden = position < 0;
        c.pick.style.order = String(position);
        c.pick.querySelector('[data-pick-btn]').setAttribute('aria-pressed', String(state.ids.indexOf(c.id) > -1));
      });
      root.querySelector('[data-count]').textContent =
        onShelf.length + (onShelf.length === 1 ? ' model' : ' models') + ' shown. Compare up to ' + MAX + '.';
      updateArrows();

      root.querySelector('[data-board-title]').textContent =
        '2.  ' + (cfg.step2 || 'Your side-by-side comparison') + ' (' + visible.length + ' of ' + MAX + ')';
      var note = root.querySelector('[data-note]');
      note.hidden = !state.closest;
      note.textContent = state.closest ? 'No model matches every need. These are the closest.' : '';

      root.querySelectorAll('[data-src-field]').forEach(function (field) { field.value = state.src || 'compare'; });

      if (EMBEDDED) return;
      var params = new URLSearchParams();
      if (!state.auto && state.ids.length) params.set('ids', state.ids.join(','));
      if (state.needs.length) params.set('needs', state.needs.join(','));
      if (state.diff) params.set('diff', '1');
      if (state.src) params.set('src', state.src);
      // Keep parameters this tool does not own (preview_theme_id, view, utm_*).
      query.forEach(function (value, name) {
        if (['ids', 'needs', 'diff', 'src'].indexOf(name) < 0) params.set(name, value);
      });
      var search = params.toString().replace(/%2C/g, ',');
      history.replaceState(null, '', window.location.pathname + (search ? '?' + search : '') + window.location.hash);
    }

    // ---- rail + table arrows ----------------------------------------------------
    function updateArrows() {
      root.querySelectorAll('[data-rail]').forEach(function (b) {
        var back = b.dataset.rail === '-1';
        b.disabled = back ? rail.scrollLeft <= 2 : rail.scrollLeft + rail.clientWidth >= rail.scrollWidth - 2;
      });
      root.querySelectorAll('[data-scroll]').forEach(function (b) {
        var back = b.dataset.scroll === '-1';
        b.disabled = back ? scroller.scrollLeft <= 2 : scroller.scrollLeft + scroller.clientWidth >= scroller.scrollWidth - 2;
      });
    }
    rail.addEventListener('scroll', updateArrows, { passive: true });
    scroller.addEventListener('scroll', updateArrows, { passive: true });
    window.addEventListener('resize', updateArrows);
    function step(el, dir) {
      var first = [].find.call(el.children, function (child) { return !child.hidden && child.offsetWidth && !child.classList.contains('c-compare__labels'); });
      var width = first ? first.getBoundingClientRect().width + 16 : el.clientWidth * 0.8;
      el.scrollBy({ left: dir * width, behavior: 'smooth' });
    }

    // ---- table edits ------------------------------------------------------------
    function add(id) {
      if (state.ids.indexOf(id) > -1) return;
      if (state.ids.length >= MAX) { open('limit'); return; }
      state.ids.push(id);
      state.auto = false;
      track('compare_product_add', { product_id: id, column: state.ids.length });
      render();
      if (!byId[id].available) { state.unavailable = id; open('unavailable'); }
    }
    function remove(id) {
      var position = state.ids.indexOf(id);
      if (position < 0) return;
      state.ids.splice(position, 1);
      state.auto = false;
      track('compare_product_remove', { product_id: id, column: position + 1 });
      render();
    }
    function replace(oldId, newId) {
      var position = state.ids.indexOf(oldId);
      if (position < 0) { add(newId); return; }
      state.ids[position] = newId;
      state.auto = false;
      track('compare_product_replace', { product_id: newId, replaced: oldId, column: position + 1 });
      render();
      if (!byId[newId].available) { state.unavailable = newId; open('unavailable'); }
    }
    function toTable() { root.querySelector('[data-compare]').scrollIntoView({ behavior: 'smooth', block: 'start' }); }
    function toRail() { root.querySelector('[data-panel]').scrollIntoView({ behavior: 'smooth', block: 'start' }); }

    // ---- add to cart --------------------------------------------------------------
    function addToCart(form) {
      var button = form.querySelector('[type="submit"]');
      var col = form.closest('[data-col]');
      lastForm = form;
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      fetch('/cart/add.js', { method: 'POST', body: new FormData(form), headers: { Accept: 'application/json' }, credentials: 'same-origin' })
        .then(function (response) { return response.ok ? response.json() : Promise.reject(response); })
        .then(function () {
          // Refresh the theme's cart drawer so the count is right; do not open it.
          document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh'));
          dialogs.added.querySelector('[data-added-name]').textContent = col.dataset.vendor + ' ' + col.dataset.name;
          open('added');
        })
        .catch(function () { open('error'); })
        .finally(function () {
          button.disabled = false;
          button.removeAttribute('aria-busy');
        });
    }

    // ---- events ----------------------------------------------------------------
    root.addEventListener('click', function (event) {
      var target = event.target;
      var chip = target.closest('[data-chip]');
      if (chip) {
        var position = state.needs.indexOf(chip.dataset.chip);
        if (position > -1) state.needs.splice(position, 1); else state.needs.push(chip.dataset.chip);
        track('compare_chip_toggle', { chip: chip.dataset.chip, on: position < 0 });
        if (state.auto) autoFill();
        rail.scrollLeft = 0;
        render();
        return;
      }
      var pickBtn = target.closest('[data-pick-btn]');
      if (pickBtn) {
        var id = pickBtn.closest('[data-pick]').dataset.pick;
        if (state.ids.indexOf(id) > -1) remove(id); else add(id);
        return;
      }
      var railBtn = target.closest('[data-rail]');
      if (railBtn) { step(rail, Number(railBtn.dataset.rail)); return; }
      var scrollBtn = target.closest('[data-scroll]');
      if (scrollBtn) { step(scroller, Number(scrollBtn.dataset.scroll)); return; }
      var rm = target.closest('[data-remove]');
      if (rm) { remove(rm.closest('[data-col]').dataset.id); return; }
      var rep = target.closest('[data-replace]');
      if (rep) { openPicker(rep.closest('[data-col]').dataset.id); return; }
      var choose = target.closest('[data-choose]');
      if (choose) {
        close(dialogs.picker);
        replace(state.replacing, choose.dataset.choose);
        state.replacing = null;
        return;
      }
      if (target.closest('[data-browse]')) { toRail(); return; }
      if (target.closest('[data-clear]')) {
        state.ids = [];
        state.auto = false;
        track('compare_clear', {});
        render();
        return;
      }
      var toggle = target.closest('[data-toggle="diff"]');
      if (toggle) {
        state.diff = !state.diff;
        track('compare_toggle', { toggle: 'diff', on: state.diff });
        render();
        return;
      }
      var opener = target.closest('[data-open]');
      if (opener) { open(opener.dataset.open); return; }
      var action = target.closest('[data-action]');
      if (action) {
        var dialog = action.closest('dialog');
        close(dialog);
        switch (action.dataset.action) {
          case 'to-table': toTable(); break;
          case 'find-another': openPicker(state.unavailable); break;
          case 'retry': if (lastForm) addToCart(lastForm); break;
        }
        return;
      }
      var pdp = target.closest('[data-pdp]');
      if (pdp) { track('compare_pdp_click', { product_id: pdp.closest('[data-col]').dataset.id, src: state.src }); return; }
      if (target.closest('[data-specialist]')) track('compare_specialist_click', { src: state.src });
      if (target.closest('[data-quiz]')) track('compare_quiz_click', { src: state.src });
    });
    root.addEventListener('submit', function (event) {
      var form = event.target.closest('form[data-compare-atc]');
      if (!form) return;
      event.preventDefault();
      track('compare_atc', { product_id: form.closest('[data-col]').dataset.id, column: state.ids.indexOf(form.closest('[data-col]').dataset.id) + 1, src: state.src });
      addToCart(form);
    });

    // ---- first paint -------------------------------------------------------------
    if (!state.ids.length && state.auto) autoFill();
    root.classList.add('is-js-ready');
    root.style.setProperty('--c-compare-levels', root.querySelectorAll('.c-compare__key-level').length);
    render();
    track('compare_view', { src: state.src, product_ids: state.ids, needs: state.needs });
  }

  // ---- PDP "How it compares" strip: tracking only, the markup is all Liquid ---
  function initStrip(strip) {
    if (strip.__cCompareStrip) return;
    strip.__cCompareStrip = true;
    var productId = strip.dataset.productId;
    if ('IntersectionObserver' in window) {
      var seen = new IntersectionObserver(function (entries) {
        if (!entries.some(function (entry) { return entry.isIntersecting; })) return;
        seen.disconnect();
        track('compare_strip_view', { product_id: productId });
      }, { threshold: 0.4 });
      seen.observe(strip);
    }
    strip.addEventListener('submit', function (event) {
      var card = event.target.closest('[data-strip-item]');
      if (card) track('compare_atc', { product_id: card.dataset.stripItem, column: Number(card.dataset.column), with_addon: false, src: 'pdp' });
    }, true);
  }

  // ---- the tool on a PDP: fetched from the comparison page, then mounted ------
  // Fetching it only when the shopper neared it left a gap of about a second
  // (the page is ~1.3 MB of HTML, ~80 KB over the wire). It is now fetched and
  // built off the page as soon as the PDP has loaded and the browser is idle,
  // so only the mount waits for the shopper. A shopper who scrolls there first
  // starts the fetch, as before.
  function whenIdle(fn) {
    function go() {
      if (window.requestIdleCallback) window.requestIdleCallback(fn, { timeout: 2000 });
      else setTimeout(fn, 200);
    }
    if (document.readyState === 'complete') go(); else window.addEventListener('load', go, { once: true });
  }
  function mountEmbed(embed) {
    if (embed.__cCompareEmbed) return;
    embed.__cCompareEmbed = true;
    var ready = null;   // resolves to the tool's root, or false when there is none
    function prepare() {
      if (ready) return ready;
      ready = fetch(embed.dataset.source, { credentials: 'same-origin' })
        .then(function (response) { return response.ok ? response.text() : Promise.reject(response); })
        .then(function (html) {
          var found = new DOMParser().parseFromString(html, 'text/html').querySelector(SELECTOR);
          if (!found) return false;   // the page is not on the tool template: keep the link
          var root = document.importNode(found, true);
          root.removeAttribute('id');
          root.dataset.embed = '1';
          root.dataset.pin = embed.dataset.pin;
          // The PDP already has its h1.
          root.querySelectorAll('h1').forEach(function (h1) {
            var h2 = document.createElement('h2');
            h2.className = h1.className;
            h2.innerHTML = h1.innerHTML;
            h1.replaceWith(h2);
          });
          return root;
        })
        // A failed prefetch is tried again when the shopper gets there.
        .catch(function () { ready = null; return null; });
      return ready;
    }
    function mount() {
      prepare()
        .then(function (root) { return root === null ? prepare() : root; })
        .then(function (root) {
          if (!root) return;   // the fallback link stays
          embed.replaceChildren(root);
          init(root);
        });
    }
    whenIdle(prepare);
    if (!('IntersectionObserver' in window)) { mount(); return; }
    var near = new IntersectionObserver(function (entries) {
      if (!entries.some(function (entry) { return entry.isIntersecting; })) return;
      near.disconnect();
      mount();
    }, { rootMargin: '800px 0px' });
    near.observe(embed);
  }

  function boot() {
    document.querySelectorAll('[data-sl-id="sl-section-compare-embed-v1"]').forEach(mountEmbed);
    document.querySelectorAll(SELECTOR).forEach(init);
    document.querySelectorAll('[data-sl-id="sl-section-compare-strip-v1"]').forEach(initStrip);
  }
  // Loaded async: start once the DOM is ready, or earlier when the comparison
  // section has already been parsed (the line at the end of the section).
  window.cCompareBoot = boot;
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
    if (window.cCompareParsed) boot();
  } else boot();
  document.addEventListener('shopify:section:load', boot);
})();
